/**
 * Runs `fn` right after the browser paints the frame that follows the current
 * task — the "yield to paint" pattern for INP. `requestAnimationFrame` fires
 * just before that paint; the `setTimeout(0)` queued from inside it runs just
 * after it. So the tap's visual response is on screen before `fn` starts, and
 * whatever `fn` costs no longer counts toward the tap's INP.
 *
 * Use it for work a tap TRIGGERS but the tap's own paint does not need:
 * analytics that are not conversions (`ga.event` → gtag measured 105 ms at 4×
 * CPU inside a search-card tap), and router bookkeeping such as the shallow
 * `Router.replace` a card runs before navigating (156 ms of Meta / Bing /
 * LinkedIn `history` hooks at 4× CPU). See
 * docs/performance/interaction-latency.md → "Android INP: the tap itself".
 *
 * Differs from `deferToIdle` in two ways:
 *   - it waits one frame, not for idle, so the work still lands within ~16 ms
 *     on a free main thread;
 *   - it runs `fn` synchronously if the page is hidden first. A tap that opens
 *     a new tab (`target="_blank"`, WhatsApp, a desktop card) backgrounds this
 *     page, where `requestAnimationFrame` stops firing. `visibilitychange` /
 *     `pagehide` flush the pending work instead of letting it stall.
 *
 * Do NOT use it to delay a conversion event (`generate_lead`, `contact_wa`,
 * `begin_checkout`, …) without a decision on record; the hidden-page flush
 * makes a drop unlikely, not impossible (a crashed or killed tab). Decision on
 * record (Jules, 2026-10-28): the WhatsApp CTAs that open WhatsApp themselves
 * (Flotillas hero, PriceCard WA pill, listing WA FAB, search-card WA pill)
 * defer their `contact_wa` / `generate_lead` fanout — opening WhatsApp hides
 * the page, which flushes it.
 */
const AFTER_PAINT_FALLBACK_MS = 500

export const runAfterNextPaint = (fn: () => void): void => {
  if (
    typeof window === 'undefined' ||
    typeof window.requestAnimationFrame !== 'function'
  ) {
    fn()
    return
  }

  let done = false
  let fallbackTimer = 0

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') run()
  }

  function run() {
    if (done) return
    done = true
    window.clearTimeout(fallbackTimer)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', run)
    fn()
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', run)
  window.requestAnimationFrame(() => {
    window.setTimeout(run, 0)
  })
  // Backstop for contexts that throttle rAF while still visible (some
  // webviews, cross-origin iframes). Never earlier than the paint on a free
  // main thread: rAF + setTimeout(0) wins by ~480 ms there.
  fallbackTimer = window.setTimeout(run, AFTER_PAINT_FALLBACK_MS)
}

/** Promise form of `runAfterNextPaint`, for `await` inside async handlers. */
export const afterNextPaint = (): Promise<void> =>
  new Promise((resolve) => runAfterNextPaint(resolve))
